// src/components/admin/sales/ProfitabilityTab.tsx
// Painel de Rentabilidade Operacional - REFATORADO GRAIA

import React, { useState } from 'react';
import {
    DollarSign,
    TrendingDown,
    TrendingUp,
    Plus,
    X,
    Edit2,
    Trash2,
    Calendar,
    Filter,
    PieChart,
    BarChart3,
    AlertTriangle
} from 'lucide-react';
import './ProfitabilityTab.css';
import {
    useProfitabilityStats,
    useProfitabilityExpenses,
    useProfitabilityChart,
    useInvalidate,
} from '../../../hooks/useAdminQueries';


interface Expense {
    _id: string;
    type: 'infra' | 'marketing' | 'tools' | 'other';
    description: string;
    amountCents: number;
    recurrence: 'monthly' | 'yearly' | 'once';
    date: string;
    notes?: string;
}

interface ProfitabilityTabProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const ProfitabilityTab: React.FC<ProfitabilityTabProps> = ({ onShowToast }) => {

    // Filtros
    const [filterType, setFilterType] = useState<string>('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [chartPeriod, setChartPeriod] = useState<'week' | 'month' | 'year'>('month');

    const { data: statsData, isLoading } = useProfitabilityStats(startDate, endDate);
    const { data: expensesData } = useProfitabilityExpenses(filterType, startDate, endDate);
    const { data: chartRes } = useProfitabilityChart(chartPeriod);
    const invalidate = useInvalidate();

    const stats = statsData ?? null;
    const expenses = expensesData?.expenses ?? [];
    const chartData = chartRes?.chartData ?? [];
    const loading = isLoading;

    // Modal de criar/editar despesa
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
    const [expenseToDelete, setExpenseToDelete] = useState<{ id: string; description: string } | null>(null);
    const [expenseForm, setExpenseForm] = useState({
        type: 'infra' as 'infra' | 'marketing' | 'tools' | 'other',
        description: '',
        amountCents: 0,
        recurrence: 'once' as 'monthly' | 'yearly' | 'once',
        date: new Date().toISOString().split('T')[0],
        notes: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleCreateExpense = () => {
        setEditingExpense(null);
        setExpenseForm({
            type: 'infra',
            description: '',
            amountCents: 0,
            recurrence: 'once',
            date: new Date().toISOString().split('T')[0],
            notes: ''
        });
        setShowExpenseModal(true);
    };

    const handleEditExpense = (expense: Expense) => {
        setEditingExpense(expense);
        setExpenseForm({
            type: expense.type,
            description: expense.description,
            amountCents: expense.amountCents,
            recurrence: expense.recurrence,
            date: new Date(expense.date).toISOString().split('T')[0],
            notes: expense.notes || ''
        });
        setShowExpenseModal(true);
    };

    const handleSubmitExpense = async () => {
        if (!expenseForm.description.trim()) {
            onShowToast('Descrição é obrigatória', 'error');
            return;
        }

        if (expenseForm.amountCents <= 0) {
            onShowToast('Valor deve ser maior que zero', 'error');
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('accessToken');

            const url = editingExpense
                ? `/api/admin/sales/profitability/expenses/${editingExpense._id}`
                : '/api/admin/sales/profitability/expenses';

            const response = await fetch(url, {
                method: editingExpense ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(expenseForm)
            });

            if (response.ok) {
                onShowToast(
                    editingExpense ? 'Despesa atualizada!' : 'Despesa registrada!',
                    'success'
                );
                setShowExpenseModal(false);
                invalidate.profitability();
            } else {
                const error = await response.json();
                onShowToast(error.message || 'Erro ao salvar despesa', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao salvar despesa', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const openDeleteModal = (expenseId: string, description: string) => {
        setExpenseToDelete({ id: expenseId, description });
        setShowDeleteModal(true);
    };

    const handleDeleteExpense = async () => {
        if (!expenseToDelete) return;

        try {
            setDeleting(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/sales/profitability/expenses/${expenseToDelete.id}`,
                {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.ok) {
                onShowToast('Despesa deletada', 'success');
                setShowDeleteModal(false);
                setExpenseToDelete(null);
                invalidate.profitability();
            } else {
                onShowToast('Erro ao deletar despesa', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao deletar despesa', 'error');
        } finally {
            setDeleting(false);
        }
    };

    const formatCurrency = (cents: number) => {
        return `R$ ${(cents / 100).toFixed(2)}`;
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('pt-BR');
    };

    const getTypeLabel = (type: string) => {
        const types: Record<string, string> = {
            infra: 'Infraestrutura',
            marketing: 'Marketing',
            tools: 'Ferramentas',
            other: 'Outros'
        };
        return types[type] || type;
    };

    const getRecurrenceLabel = (recurrence: string) => {
        const labels: Record<string, string> = {
            monthly: 'Mensal',
            yearly: 'Anual',
            once: 'Único'
        };
        return labels[recurrence] || recurrence;
    };

    if (loading) {
        return (
            <div className="pt-loading">
                <div className="pt-spinner"></div>
                <p>Carregando dados de rentabilidade...</p>
            </div>
        );
    }

    if (!stats) {
        return <div className="pt-error">Erro ao carregar dados</div>;
    }

    return (
        <div className="pt-container">
            {/* HEADER */}
            <div className="pt-header">
                <div>
                    <h2>Rentabilidade Operacional</h2>
                    <p className="pt-subtitle">
                        Controle de custos e unit economics da plataforma
                    </p>
                </div>
                <button className="pt-btn-new-expense" onClick={handleCreateExpense}>
                    <Plus size={18} />
                    Nova Despesa
                </button>
            </div>

            {/* CARDS PRINCIPAIS - 2x2 */}
            <div className="pt-cards-container">
                <div className="pt-cards-grid">
                    <div className="pt-card">
                        <div className="pt-card-icon" style={{ background: '#003BD1' }}>
                            <DollarSign size={28} />
                        </div>
                        <div className="pt-card-info">
                            <span className="pt-card-label">Receita Total</span>
                            <span className="pt-card-value">
                                {formatCurrency(stats.totalRevenueCents)}
                            </span>
                            <span className="pt-card-description">
                                {stats.totalSales} vendas aprovadas
                            </span>
                        </div>
                    </div>

                    <div className="pt-card pt-highlight">
                        <div className="pt-card-icon" style={{ background: '#10b981' }}>
                            <TrendingUp size={28} />
                        </div>
                        <div className="pt-card-info">
                            <span className="pt-card-label">Lucro Parcial</span>
                            <span className="pt-card-value">
                                {formatCurrency(stats.partialProfitCents)}
                            </span>
                            <span className="pt-card-description">Receita - Comissões</span>
                        </div>
                    </div>

                    <div className="pt-card">
                        <div className="pt-card-icon" style={{ background: '#f59e0b' }}>
                            <TrendingDown size={28} />
                        </div>
                        <div className="pt-card-info">
                            <span className="pt-card-label">Despesas</span>
                            <span className="pt-card-value pt-expense">
                                {formatCurrency(stats.totalExpensesCents)}
                            </span>
                            <span className="pt-card-description">Custos operacionais</span>
                        </div>
                    </div>

                    <div className={`pt-card ${stats.realProfitCents >= 0 ? 'pt-positive' : 'pt-negative'}`}>
                        <div className="pt-card-icon" style={{
                            background: stats.realProfitCents >= 0 ? '#003BD1' : '#ef4444'
                        }}>
                            {stats.realProfitCents >= 0 ? <TrendingUp size={28} /> : <TrendingDown size={28} />}
                        </div>
                        <div className="pt-card-info">
                            <span className="pt-card-label">Lucro Real</span>
                            <span className={`pt-card-value ${stats.realProfitCents >= 0 ? '' : 'pt-negative-value'}`}>
                                {formatCurrency(stats.realProfitCents)}
                            </span>
                            <span className="pt-card-description">
                                Margem: {stats.unitEconomics.profitMargin}%
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* UNIT ECONOMICS */}
            <div className="pt-economics-container">
                <h3>
                    <PieChart size={20} />
                    Unit Economics
                </h3>
                <div className="pt-economics-grid">
                    <div className="pt-economics-card">
                        <span className="pt-economics-label">Custo por Venda</span>
                        <span className="pt-economics-value">
                            {formatCurrency(stats.unitEconomics.avgExpensePerSale)}
                        </span>
                        <div className="pt-economics-breakdown">
                            <span className="pt-breakdown-item">
                                Fixo: {formatCurrency(Math.round(stats.unitEconomics.avgExpensePerSale * 0.4))}
                            </span>
                            <span className="pt-breakdown-item">
                                Variável: {formatCurrency(Math.round(stats.unitEconomics.avgExpensePerSale * 0.6))}
                            </span>
                        </div>
                    </div>

                    <div className="pt-economics-card">
                        <span className="pt-economics-label">Receita por Venda</span>
                        <span className="pt-economics-value">
                            {formatCurrency(stats.unitEconomics.avgRevenuePerSale)}
                        </span>
                    </div>

                    <div className="pt-economics-card pt-economics-highlight">
                        <span className="pt-economics-label">Lucro por Venda</span>
                        <span className="pt-economics-value">
                            {formatCurrency(stats.unitEconomics.avgProfitPerSale)}
                        </span>
                        <span className="pt-economics-margin">
                            Margem: {stats.unitEconomics.profitMargin}%
                        </span>
                    </div>
                </div>

                {stats.expensesByCategory.length > 0 && (
                    <div className="pt-efficiency-alert">
                        <AlertTriangle size={16} />
                        <span>
                            Marketing representa{' '}
                            {(() => {
                                const marketing = (stats.expensesByCategory as Array<{ _id: string; total: number }>).find(c => c._id === 'marketing');
                                return (((marketing?.total || 0) / stats.totalExpensesCents) * 100).toFixed(1);
                            })()}%
                            {' '}do custo total
                        </span>
                    </div>
                )}
            </div>

            {/* GRÁFICO - PROPORCIONAL */}
            <div className="pt-chart-container">
                <div className="pt-chart-header">
                    <h3>
                        <BarChart3 size={20} />
                        Evolução Financeira
                    </h3>
                    <div className="pt-chart-period-selector">
                        <button
                            className={chartPeriod === 'week' ? 'active' : ''}
                            onClick={() => setChartPeriod('week')}
                        >
                            7 dias
                        </button>
                        <button
                            className={chartPeriod === 'month' ? 'active' : ''}
                            onClick={() => setChartPeriod('month')}
                        >
                            30 dias
                        </button>
                        <button
                            className={chartPeriod === 'year' ? 'active' : ''}
                            onClick={() => setChartPeriod('year')}
                        >
                            12 meses
                        </button>
                    </div>
                </div>

                <div className="pt-chart-wrapper">
                    {chartData.length === 0 ? (
                        <div className="pt-chart-empty">
                            <BarChart3 size={48} />
                            <p>Nenhum dado neste período</p>
                        </div>
                    ) : (
                        <>
                            <div className="pt-chart-bars">
                                {chartData.map((item: any, index: number) => {
                                    // Calcular altura proporcional
                                    const maxValue = Math.max(
                                        ...chartData.map((d: any) => Math.max(d.revenue, d.expenses, Math.abs(d.realProfit)))
                                    );

                                    const revenueHeight = maxValue > 0 ? (item.revenue / maxValue) * 100 : 0;
                                    const expensesHeight = maxValue > 0 ? (item.expenses / maxValue) * 100 : 0;
                                    const profitHeight = maxValue > 0 ? (Math.abs(item.realProfit) / maxValue) * 100 : 0;

                                    return (
                                        <div key={index} className="pt-chart-group">
                                            <div className="pt-chart-bars-stack">
                                                <div
                                                    className="pt-chart-bar pt-bar-revenue"
                                                    style={{ height: `${Math.max(revenueHeight, 5)}%` }}
                                                    title={`Receita: ${formatCurrency(item.revenue)}`}
                                                />
                                                <div
                                                    className="pt-chart-bar pt-bar-expenses"
                                                    style={{ height: `${Math.max(expensesHeight, 5)}%` }}
                                                    title={`Despesas: ${formatCurrency(item.expenses)}`}
                                                />
                                                <div
                                                    className={`pt-chart-bar ${item.realProfit >= 0 ? 'pt-bar-profit-positive' : 'pt-bar-profit-negative'}`}
                                                    style={{ height: `${Math.max(profitHeight, 5)}%` }}
                                                    title={`Lucro: ${formatCurrency(item.realProfit)}`}
                                                />
                                            </div>
                                            <span className="pt-chart-label">
                                                {item.period.split('-').slice(1).join('/')}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="pt-chart-legend">
                                <div className="pt-legend-item">
                                    <span className="pt-legend-color pt-legend-revenue"></span>
                                    Receita
                                </div>
                                <div className="pt-legend-item">
                                    <span className="pt-legend-color pt-legend-expenses"></span>
                                    Despesas
                                </div>
                                <div className="pt-legend-item">
                                    <span className="pt-legend-color pt-legend-profit"></span>
                                    Lucro Real
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* DESPESAS */}
            <div className="pt-expenses-container">
                <div className="pt-expenses-header">
                    <h3>Despesas Registradas</h3>
                    <div className="pt-expenses-filters">
                        <div className="pt-filter-group">
                            <Filter size={16} />
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="all">Todos os tipos</option>
                                <option value="infra">Infraestrutura</option>
                                <option value="marketing">Marketing</option>
                                <option value="tools">Ferramentas</option>
                                <option value="other">Outros</option>
                            </select>
                        </div>

                        <div className="pt-date-filter">
                            <Calendar size={16} />
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                placeholder="Data inicial"
                            />
                            <span>até</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                placeholder="Data final"
                            />
                        </div>

                        {(filterType !== 'all' || startDate || endDate) && (
                            <button
                                className="pt-btn-clear-filters"
                                onClick={() => {
                                    setFilterType('all');
                                    setStartDate('');
                                    setEndDate('');
                                }}
                            >
                                <X size={16} />
                                Limpar
                            </button>
                        )}
                    </div>
                </div>

                {expenses.length === 0 ? (
                    <div className="pt-expenses-empty">
                        <TrendingDown size={64} />
                        <h3>Nenhuma despesa registrada</h3>
                        <p>Comece registrando seus custos operacionais</p>
                    </div>
                ) : (
                    <div className="pt-expenses-table-wrapper">
                        <table className="pt-expenses-table">
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th>Descrição</th>
                                    <th>Valor</th>
                                    <th>Recorrência</th>
                                    <th>Data</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.map((expense: Expense) => (
                                    <tr key={expense._id}>
                                        <td>
                                            <span className={`pt-type-badge pt-type-${expense.type}`}>
                                                {getTypeLabel(expense.type)}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="pt-expense-description">
                                                <strong>{expense.description}</strong>
                                                {expense.notes && (
                                                    <span className="pt-expense-notes">{expense.notes}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="pt-value pt-expense-value">
                                            {formatCurrency(expense.amountCents)}
                                        </td>
                                        <td>
                                            <span className={`pt-recurrence-badge pt-recurrence-${expense.recurrence}`}>
                                                {getRecurrenceLabel(expense.recurrence)}
                                            </span>
                                        </td>
                                        <td>{formatDate(expense.date)}</td>
                                        <td>
                                            <div className="pt-expense-actions">
                                                <button
                                                    className="pt-btn-icon pt-btn-edit"
                                                    onClick={() => handleEditExpense(expense)}
                                                    title="Editar"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    className="pt-btn-icon pt-btn-delete"
                                                    onClick={() => openDeleteModal(expense._id, expense.description)}
                                                    title="Deletar"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* MODAL: CRIAR/EDITAR DESPESA */}
            {showExpenseModal && (
                <div className="pt-modal-overlay" onClick={() => setShowExpenseModal(false)}>
                    <div className="pt-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="pt-modal-header">
                            <h3>{editingExpense ? 'Editar Despesa' : 'Nova Despesa'}</h3>
                            <button
                                className="pt-btn-close-modal"
                                onClick={() => setShowExpenseModal(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="pt-modal-body">
                            <div className="pt-form-group">
                                <label>Tipo *</label>
                                <select
                                    value={expenseForm.type}
                                    onChange={(e) => setExpenseForm({
                                        ...expenseForm,
                                        type: e.target.value as any
                                    })}
                                    className="pt-form-input"
                                >
                                    <option value="infra">Infraestrutura</option>
                                    <option value="marketing">Marketing</option>
                                    <option value="tools">Ferramentas</option>
                                    <option value="other">Outros</option>
                                </select>
                            </div>

                            <div className="pt-form-group">
                                <label>Descrição *</label>
                                <input
                                    type="text"
                                    value={expenseForm.description}
                                    onChange={(e) => setExpenseForm({
                                        ...expenseForm,
                                        description: e.target.value
                                    })}
                                    placeholder="Ex: Servidor AWS, Google Ads, Domínio"
                                    className="pt-form-input"
                                />
                            </div>

                            <div className="pt-form-row">
                                <div className="pt-form-group">
                                    <label>Valor (R$) *</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={(expenseForm.amountCents / 100).toFixed(2)}
                                        onChange={(e) => setExpenseForm({
                                            ...expenseForm,
                                            amountCents: Math.round(parseFloat(e.target.value || '0') * 100)
                                        })}
                                        placeholder="0.00"
                                        className="pt-form-input"
                                    />
                                </div>

                                <div className="pt-form-group">
                                    <label>Recorrência *</label>
                                    <select
                                        value={expenseForm.recurrence}
                                        onChange={(e) => setExpenseForm({
                                            ...expenseForm,
                                            recurrence: e.target.value as any
                                        })}
                                        className="pt-form-input"
                                    >
                                        <option value="once">Único</option>
                                        <option value="monthly">Mensal</option>
                                        <option value="yearly">Anual</option>
                                    </select>
                                </div>
                            </div>

                            <div className="pt-form-group">
                                <label>Data *</label>
                                <input
                                    type="date"
                                    value={expenseForm.date}
                                    onChange={(e) => setExpenseForm({
                                        ...expenseForm,
                                        date: e.target.value
                                    })}
                                    className="pt-form-input"
                                />
                            </div>

                            <div className="pt-form-group">
                                <label>Observações (opcional)</label>
                                <textarea
                                    value={expenseForm.notes}
                                    onChange={(e) => setExpenseForm({
                                        ...expenseForm,
                                        notes: e.target.value
                                    })}
                                    placeholder="Informações adicionais sobre esta despesa"
                                    className="pt-form-textarea"
                                    rows={3}
                                />
                            </div>
                        </div>

                        <div className="pt-modal-footer">
                            <button
                                className="pt-btn-cancel"
                                onClick={() => setShowExpenseModal(false)}
                                disabled={submitting}
                            >
                                Cancelar
                            </button>
                            <button
                                className="pt-btn-confirm"
                                onClick={handleSubmitExpense}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <div className="pt-spinner-small"></div>
                                        Salvando...
                                    </>
                                ) : (
                                    editingExpense ? 'Atualizar' : 'Criar Despesa'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: CONFIRMAR EXCLUSÃO */}
            {showDeleteModal && expenseToDelete && (
                <div className="pt-modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="pt-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="pt-modal-header">
                            <h3>Confirmar Exclusão</h3>
                            <button
                                className="pt-btn-close-modal"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="pt-modal-body">
                            <div className="pt-delete-icon">
                                <Trash2 size={48} />
                            </div>
                            <p className="pt-delete-text">
                                Tem certeza que deseja <strong>EXCLUIR</strong> esta despesa?
                            </p>
                            <div className="pt-delete-details">
                                <div className="pt-detail-row">
                                    <span>Despesa:</span>
                                    <strong>{expenseToDelete.description}</strong>
                                </div>
                            </div>
                            <div className="pt-warning-box">
                                <AlertTriangle size={14} />
                                <span>Esta ação é <strong>irreversível</strong>.</span>
                            </div>
                        </div>

                        <div className="pt-modal-footer">
                            <button
                                className="pt-btn-cancel"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleting}
                            >
                                Cancelar
                            </button>
                            <button
                                className="pt-btn-danger"
                                onClick={handleDeleteExpense}
                                disabled={deleting}
                            >
                                {deleting ? (
                                    <>
                                        <div className="pt-spinner-small"></div>
                                        Excluindo...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} />
                                        Excluir
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfitabilityTab;
